import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import DashboardPage from './pages/Dashboard';
import CoursesPage from './pages/Registrations';
import CourseDetailPage from './pages/ProjectEvaluation';
import ProfilePage from './pages/Profile';
import AuthPage from './pages/Auth';
import MyCertificatesPage from './pages/CertificateGenerator';
import WishlistPage from './pages/Placements';
import ReviewsPage from './pages/Analytics';
import QuizAttemptsPage from './pages/MockInterview';
import OrderHistoryPage from './pages/AllCandidates';
import ReferralsPage from './pages/CreateBatchPage';
import MessagesPage from './pages/DirectEnquiries';
import SupportTicketsPage from './pages/SocialMediaEnquiries';
import SettingsPage from './pages/AssignCandidates';
import KanbanBoardPage from './pages/KanbanBoard';
import MySchedulePage from './pages/MySchedule';
import AttendancePage from './pages/Attendance';
import FeedbackPage from './pages/Feedback';
import AlumniPage from './pages/Alumni';
import AlumniDetailPage from './pages/AlumniDetailPage';


import { ToastProvider } from './components/Toaster';
import { User } from './types';
import { mockUsers } from './mockData';
import { AuthContext } from './contexts/AuthContext';
import { loginUser } from './services/AuthService.jsx';

const App: React.FC = () => {
  // Initialize state from localStorage
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Could not parse user from localStorage", e);
        return null;
      }
    }
    return null;
  });

  const login = async (email: string, pass: string): Promise<User> => {
    try {
      const data = await loginUser(email, pass);

      if (data && data.userMode && typeof data.userMode === 'string') {
        const userMode = data.userMode.trim().toLowerCase();
        const mockUser = mockUsers.find(u => u.mode === userMode);
        
        if (mockUser) {
          const userToLogin: User = {
            ...mockUser,
            email: data.email || email, 
            mode: data.userMode, 
          };

          if (data.accessToken) localStorage.setItem("accessToken", data.accessToken);
          if (data.TraineeToken) localStorage.setItem("TraineeToken", data.TraineeToken);
          if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);

          localStorage.setItem('currentUser', JSON.stringify(userToLogin));
          setCurrentUser(userToLogin);
          return userToLogin;
        }
      }
       throw new Error("Invalid user data or API response.");
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("currentUser");
  };

  return (
    <AuthContext.Provider value={{ currentUser, login, logout }}>
      <ToastProvider>
        <Routes>
          <Route path="/auth/*" element={<AuthPage />} />
          <Route 
            path="/*" 
            element={
              currentUser ? (
                <Layout userMode={currentUser.mode}>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/courses" element={<CoursesPage />} />
                    <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/certificates" element={<MyCertificatesPage />} />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route path="/quiz-attempts" element={<QuizAttemptsPage />} />
                    <Route path="/order-history" element={<OrderHistoryPage />} />
                    <Route path="/referrals" element={<ReferralsPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/support-tickets" element={<SupportTicketsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    {/* Offline Routes */}
                    <Route path="/kanban" element={<KanbanBoardPage />} />
                    <Route path="/schedule" element={<MySchedulePage />} />
                    <Route path="/attendance" element={<AttendancePage />} />
                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/alumni" element={<AlumniPage />} />
                    <Route path="/alumni/:id" element={<AlumniDetailPage />} />
                    <Route path="*" element={<Navigate to={currentUser.mode === 'offline' ? '/kanban' : '/dashboard'} />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/auth/login" />
              )
            } 
          />
        </Routes>
      </ToastProvider>
    </AuthContext.Provider>
  );
};

export default App;